import { useCallback, useMemo, useState, createElement } from 'react';
import type { TFunction } from 'i18next';
import { Badge } from '@/components/ui/badge';
import type { DataTableColumn } from '@/shared/components/data-table';
import type { UpcomingLine } from '../types';
import {
  PROBE_TYPES,
  PROBE_TYPE_VARIANT,
  PROVIDER_VARIANT,
  TREATMENT_ACTIONS,
  ACTION_VARIANT,
  probeTypeLabel,
  serviceModeLabel,
  actionLabel,
  providerLabel,
} from '../labels';

export type UpcomingLineColumn = DataTableColumn<UpcomingLine>;

/**
 * Client-side sort + filter state for the #991 "Upcoming" per-probe table.
 * Clone of useExpiredLinesTable, retyped to UpcomingLine and defaulting to the
 * soonest-due-first sort. Column labels + badge text are localized via `t`.
 */
export function useUpcomingLinesTable(lines: UpcomingLine[], t: TFunction) {
  const columns = useMemo<UpcomingLineColumn[]>(() => [
    {
      key: 'probeType',
      label: t('calibrationPlanning.columns.probeType', 'Probe type'),
      sortKey: 'probeType',
      filterType: 'select',
      filterAllLabel: t('calibrationPlanning.columns.allTypes', 'All types'),
      filterOptions: PROBE_TYPES.map((code) => ({ label: probeTypeLabel(t, code), value: code })),
      getValue: (row) => row.probeType ?? '',
      render: (row) =>
        row.probeType
          ? createElement(Badge, { variant: PROBE_TYPE_VARIANT[row.probeType] }, probeTypeLabel(t, row.probeType))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'serviceMode',
      label: t('calibrationPlanning.columns.serviceMode', 'Service mode'),
      sortKey: 'serviceMode',
      filterType: 'select',
      filterAllLabel: t('calibrationPlanning.columns.allModes', 'All modes'),
      filterOptions: [
        { label: serviceModeLabel(t, 'onsite'), value: 'onsite' },
        { label: serviceModeLabel(t, 'mail'), value: 'mail' },
      ],
      getValue: (row) => row.serviceMode ?? '',
      render: (row) =>
        row.serviceMode
          ? createElement(Badge, { variant: row.serviceMode === 'onsite' ? 'purple' : 'secondary' }, serviceModeLabel(t, row.serviceMode))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      // #1118 — replaces the old "What ships" column. That one had only two values and
      // said nothing at all for an on-site visit (shipKind is null whenever mode is
      // 'onsite'), so it could not answer "comment les traiter".
      key: 'action',
      label: t('calibrationPlanning.columns.action', 'Treatment'),
      sortKey: 'action',
      filterType: 'select',
      filterAllLabel: t('calibrationPlanning.columns.allActions', 'All treatments'),
      filterOptions: TREATMENT_ACTIONS.map((a) => ({ label: actionLabel(t, a), value: a })),
      getValue: (row) => row.action ?? '',
      render: (row) =>
        row.action
          ? createElement(Badge, { variant: ACTION_VARIANT[row.action] }, actionLabel(t, row.action))
          : createElement('span', { className: 'text-muted-foreground' }, '—'),
    },
    {
      key: 'provider',
      label: t('calibrationPlanning.columns.provider', 'Calibrated by'),
      sortKey: 'provider',
      filterType: 'select',
      filterAllLabel: t('calibrationPlanning.columns.allProviders', 'All providers'),
      filterOptions: [
        { label: providerLabel(t, 'atek'), value: 'atek' },
        { label: providerLabel(t, 'client'), value: 'client' },
        { label: providerLabel(t, 'other'), value: 'other' },
      ],
      getValue: (row) => row.provider,
      render: (row) => createElement(Badge, { variant: PROVIDER_VARIANT[row.provider] }, providerLabel(t, row.provider)),
    },
    {
      key: 'account',
      label: t('calibrationPlanning.columns.account', 'Account'),
      sortKey: 'account',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: t('calibrationPlanning.columns.searchAccount', 'Search account...'),
      getValue: (row) => row.account,
    },
    {
      key: 'siteName',
      label: t('calibrationPlanning.columns.site', 'Site'),
      sortKey: 'siteName',
      filterType: 'text',
      getValue: (row) => row.siteName ?? '',
    },
    {
      key: 'sensor',
      label: t('calibrationPlanning.columns.sensor', 'Sensor'),
      sortKey: 'sensor',
      filterType: 'text',
      getValue: (row) => row.sensor ?? '',
    },
    {
      key: 'channel',
      label: t('calibrationPlanning.columns.channel', 'Channel'),
      filterType: 'text',
      getValue: (row) => row.channel ?? '',
    },
    {
      key: 'probeModel',
      label: t('calibrationPlanning.columns.model', 'Model'),
      filterType: 'text',
      getValue: (row) => row.probeModel ?? '',
    },
    {
      key: 'region',
      label: t('calibrationPlanning.columns.region', 'Region'),
      sortKey: 'region',
      filterType: 'text',
      getValue: (row) => row.region ?? '',
    },
    {
      key: 'room',
      label: t('calibrationPlanning.columns.room', 'Room'),
      filterType: 'text',
      getValue: (row) => row.room ?? '',
    },
    {
      key: 'dueDate',
      label: t('calibrationPlanning.columns.dueDate', 'Due date'),
      sortKey: 'dueDate',
      filterType: 'text',
      getValue: (row) => row.dueDate,
    },
    {
      key: 'daysUntilDue',
      label: t('calibrationPlanning.columns.inDays', 'In N days'),
      sortKey: 'daysUntilDue',
      className: 'text-right tabular-nums',
      noFilter: true,
      getValue: (row) => String(row.daysUntilDue),
    },
  ], [t]);

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string>('daysUntilDue');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1); // soonest due first

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSort = useCallback((key: string) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDirection((d) => (d === 1 ? -1 : 1));
        return prevKey;
      }
      setSortDirection(1);
      return key;
    });
  }, []);

  const clearFilters = useCallback(() => setColumnFilters({}), []);

  const filteredLines = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return lines;
    return lines.filter((line) =>
      active.every(([colKey, value]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col) return true;
        const raw = col.getValue(line, undefined);
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(value) as string[];
            return selected.length === 0 || selected.includes(String(raw ?? ''));
          } catch {
            return true;
          }
        }
        return String(raw ?? '').toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [lines, columnFilters, columns]);

  const sortedLines = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey || c.key === sortKey);
    if (!col) return filteredLines;
    const dir = sortDirection;
    return [...filteredLines].sort((a, b) => {
      const va = col.getValue(a, undefined);
      const vb = col.getValue(b, undefined);
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && va !== '' && vb !== '') {
        if (na === nb) return 0;
        return na > nb ? dir : -dir;
      }
      if (va === vb) return 0;
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      return va > vb ? dir : -dir;
    });
  }, [filteredLines, sortKey, sortDirection, columns]);

  return {
    columns,
    columnFilters,
    setColumnFilter,
    sortKey,
    sortDirection,
    handleSort,
    clearFilters,
    sortedLines,
  };
}
